import { useState } from 'react';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';

interface SyncCounts {
  created: number;
  updated: number;
  removed: number;
}

interface SyncRun {
  SyncRunId: number;
  StartedUtc: string;
  FinishedUtc: string | null;
  RunBy: string | null;
  Succeeded: boolean;
  Summary: { counts: Record<string, SyncCounts>; notes: string[] } | null;
  Error: string | null;
}

interface SyncState {
  configured: boolean;
  lastRun: SyncRun | null;
}

/* The order the summary reads in, and what each count is called. */
const LABELS: [key: string, one: string, many: string][] = [
  ['team', 'team', 'teams'],
  ['meeting', 'meeting', 'meetings'],
  ['measurable', 'measurable', 'measurables'],
  ['score', 'score', 'scores'],
  ['rock', 'rock', 'rocks'],
  ['milestone', 'milestone', 'milestones'],
  ['issue', 'issue', 'issues'],
  ['todo', 'to-do', 'to-dos'],
  ['headline', 'headline', 'headlines'],
  ['person', 'person', 'people'],
  ['assessment', 'assessment', 'assessments'],
];

const when = (iso: string) =>
  new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

/** What changed, e.g. "12 rocks added, 30 updated · 4 issues removed". */
function describe(counts: Record<string, SyncCounts>): string {
  const parts = LABELS.flatMap(([key, one, many]) => {
    const c = counts[key];
    if (!c) return [];
    const bits = [
      c.created ? `${c.created} added` : '',
      c.updated ? `${c.updated} updated` : '',
      c.removed ? `${c.removed} removed` : '',
    ].filter(Boolean);
    if (!bits.length) return [];
    const total = c.created + c.updated + c.removed;
    return [`${total === 1 ? one : many}: ${bits.join(', ')}`];
  });
  return parts.length ? parts.join(' · ') : 'Already up to date.';
}

/**
 * The Sync button: brings everything across from eos.cmittechs.com (EOS Lite)
 * and says what it changed. eos.cmittechs.com wins — see api/src/lib/eosSync.ts.
 * Admins run it; everybody sees when it last ran.
 *
 * `path` is which sync: the EOS module's, or the OML page's (`/assessments/sync`,
 * api/src/lib/omlSync.ts). Both answer the same shape.
 */
export function EosSyncButton({
  isAdmin,
  onSynced,
  path = '/eos/sync',
}: {
  isAdmin: boolean;
  onSynced: () => void;
  path?: string;
}) {
  const state = useResource<SyncState>(path);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* What the sync just did, shown until the next one. */
  const [result, setResult] = useState<SyncRun['Summary']>(null);

  if (!state.data?.configured) return null;
  const last = state.data.lastRun;

  const sync = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const run = await api.post<SyncRun>(path);
      setResult(run.Summary);
      onSynced();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      state.reload();
    }
  };

  return (
    <div className="eos-sync">
      {isAdmin && (
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={sync}>
          {busy ? 'Syncing…' : 'Sync from eos.cmittechs.com'}
        </button>
      )}
      {error ? (
        <span className="xs" style={{ color: 'var(--status-danger)' }}>
          Sync failed: {error}
        </span>
      ) : last ? (
        <span className="xs muted" title={last.Summary ? [describe(last.Summary.counts), ...last.Summary.notes].join('\n') : undefined}>
          {last.FinishedUtc === null
            ? `Sync running since ${when(last.StartedUtc)}`
            : last.Succeeded
              ? `Synced ${when(last.FinishedUtc)}${last.RunBy ? ` by ${last.RunBy}` : ''}`
              : `Last sync failed ${when(last.FinishedUtc)}: ${last.Error ?? 'unknown error'}`}
        </span>
      ) : (
        <span className="xs muted">Not synced yet</span>
      )}
      {result && (
        <span className="xs">
          {[describe(result.counts), ...result.notes].join(' ')}
        </span>
      )}
    </div>
  );
}
